/**
 * MainPage.tsx - Main Application Page
 *
 * Handles authentication gating, permission checks, sidebar layout, and chat routing.
 */

import { useState, useEffect } from 'react'
import { useAuthContext } from '@/contexts/AuthContext'
import { useChatContext } from '@/contexts/ChatContext'
import { useCircleContext } from '@/contexts/CircleContext'
import { ChatView } from '@/ui/views/ChatView'
import { Card, Heading, Box, HStack, useBreakpointValue } from "@chakra-ui/react"
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Header } from '@/ui/layout/Header/Header'
import { Authentication } from '@/ui/views/authentication/Authentication'
import { useDelay } from '@/utilities/useDelay'
import { RecentChatsView } from '@/ui/views/RecentChatsView'
import { PermissionDeniedView } from '@/ui/views/PermissionDeniedView'
import { toaster } from '@/ui/components/ui/toaster'
import { ConfirmationDialog } from '@/ui/components/ConfirmationDialog'

const LoadingPage = () => (
    <Card.Root className="fade" margin="auto 1rem auto" maxWidth="100%" variant="subtle" borderRadius="1rem" alignSelf="center">
        <Card.Body padding="4">
            <Heading textAlign="center" size="sm" color="white">Loading...</Heading>
        </Card.Body>
    </Card.Root>
);

export const MainPage = () => {
    const { isAuthenticated, loading } = useAuthContext();
    const shouldTriggerAuthDelay = isAuthenticated === false;
    const showAuth = useDelay(shouldTriggerAuthDelay, 2000);
    const { postId, setPostId, recentKey } = useChatContext();
    const { deletePost, permissionDenied, circleName, requestGroupAccess } = useCircleContext();
    const { i18n, t } = useTranslation();
    const { circleId, chatId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState<any>(null);
    const [onDeleteSuccessCallback, setOnDeleteSuccessCallback] = useState<(() => void) | null>(null);

    const isRTL = i18n.dir() === 'rtl';
    const isMobile = useBreakpointValue({ base: true, md: false });

    useEffect(() => {
        const pathname = location.pathname;
        if (pathname.endsWith('/new')) {
            if (postId !== '') setPostId('');
        } else if (chatId && chatId !== postId) {
            setPostId(chatId);
        } else if (!pathname.endsWith('/new') && !pathname.includes('/chat/')) {
            navigate(`/aichat/${circleId}/new`, { replace: true });
        }
    }, [chatId, circleId, location.pathname, navigate, postId, setPostId]);

    useEffect(() => {
        if (isMobile) setSidebarOpen(false);
    }, [isMobile]);

    const handleDeleteClick = (post: any, onDeleteSuccess: () => void) => {
        setPostToDelete(post);
        setOnDeleteSuccessCallback(() => onDeleteSuccess);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!postToDelete) return;
        try {
            const isDeletingActiveChat = postToDelete._id === postId;
            await deletePost(postToDelete._id);
            if (onDeleteSuccessCallback) onDeleteSuccessCallback();
            if (isDeletingActiveChat) navigate(`/aichat/${circleId}/new`, { replace: true });
            toaster.create({ title: t("chat_deleted_successfully"), type: "success", duration: 3000 });
            setDeleteDialogOpen(false);
            setPostToDelete(null);
            setOnDeleteSuccessCallback(null);
        } catch (error) {
            toaster.create({ title: t("error_deleting_chat"), type: "error", duration: 3000 });
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setPostToDelete(null);
        setOnDeleteSuccessCallback(null);
    };

    if (loading || (isAuthenticated === undefined) || (isAuthenticated === false && !showAuth)) {
        return <LoadingPage />;
    }

    return (
        <>
            {!isAuthenticated ? (
                <Authentication />
            ) : permissionDenied ? (
                <PermissionDeniedView circleName={circleName} requestGroupAccess={requestGroupAccess} />
            ) : (
                <HStack alignItems="stretch" width="100%" maxWidth="100vw" height="100dvh" gap={0} position="relative" boxSizing="border-box" overflow="hidden">
                    {/* Backdrop for mobile */}
                    {isMobile && sidebarOpen && (
                        <Box position="fixed" top="0" left="0" right="0" bottom="0" background="rgba(0, 0, 0, 0.5)" zIndex="98" onClick={() => setSidebarOpen(false)} />
                    )}

                    {/* Sidebar - no border, teal background */}
                    <Box
                        width={sidebarOpen ? "260px" : "0"}
                        height="100dvh"
                        overflow="hidden"
                        transition="width 0.3s ease-in-out, transform 0.3s ease-in-out"
                        display="flex"
                        flexDirection="column"
                        position={{ base: "fixed", md: "relative" }}
                        zIndex={{ base: "99", md: "auto" }}
                        left={isRTL ? "auto" : "0"}
                        right={isRTL ? "0" : "auto"}
                        top="0"
                        transform={{ base: sidebarOpen ? "translateX(0)" : isRTL ? "translateX(100%)" : "translateX(-100%)", md: "translateX(0)" }}
                    >
                        <Box opacity={sidebarOpen ? 1 : 0} transition="opacity 0.2s ease-in-out" height="100%" overflowY="auto">
                            <RecentChatsView
                                rootDrawer={{ setOpen: () => {} }}
                                key={recentKey}
                                setPostId={setPostId}
                                onDeleteClick={handleDeleteClick}
                            />
                        </Box>
                    </Box>

                    {/* Main Content Area */}
                    <Box flex="1" display="flex" flexDirection="column" height="100dvh" overflow="hidden" width="100%" maxWidth="100vw" position="relative" boxSizing="border-box">
                        <Header setPostId={setPostId} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                        <Box flex="1" overflow="hidden" position="relative" width="100%" maxWidth="100%" boxSizing="border-box">
                            <ChatView />
                        </Box>
                    </Box>
                </HStack>
            )}

            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("confirm_deletion")}
                message={t("delete_chat_confirmation_message")}
                confirmText={t("delete")}
                cancelText={t("cancel")}
            />
        </>
    );
};